import type { PageDetailResponse } from "contracts/api/dashboard/page-detail.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { HEALTH_STATE } from "contracts/enums/core";
import type { PageMapLens } from "../../enums/map";
import { PAGE_MAP_LENS } from "../../enums/map";
import { isOpen, rankBySeverity } from "../website/issueModel";
import { lensLabel } from "./estateViewport";
import {
  isImportantPath,
  issueLinkMatchesPage,
  uniqueCurrentPagesForIssue,
} from "./issuePageResolution";
import { siteSpecificRecommendationsForIssue } from "./issueText";
import {
  importantPagePaths,
  pageContentFitnessScore,
  pageHealthClass,
} from "./scanModel";

type PageContext = {
  page: OverviewResponse["pages"][number];
  issues: Issue[];
  topIssue: Issue | null;
  topIssueUniqueAffectedPageCount: number;
  weak: boolean;
  strategic: boolean;
  lens: PageMapLens;
  screenshotCount: number | null;
  hasEvidence: boolean | null;
  recommendation: string;
};

export function buildPageContext(
  data: OverviewResponse,
  page: OverviewResponse["pages"][number],
  detail?: PageDetailResponse,
  lens: PageMapLens = PAGE_MAP_LENS.HUMAN,
): PageContext {
  const issues = issuesForPage(data, page);
  const topIssue = issues[0] ?? null;
  return {
    page,
    issues,
    topIssue,
    topIssueUniqueAffectedPageCount: topIssue
      ? uniqueCurrentPagesForIssue(data, topIssue).length
      : 0,
    weak: isWeakPage(page, lens),
    strategic: isStrategicPage(data, page),
    lens,
    screenshotCount: detail ? detail.screenshots.length : null,
    hasEvidence: detail ? pageDetailHasEvidence(detail) : null,
    recommendation:
      (topIssue
        ? siteSpecificRecommendationsForIssue(data, topIssue)[0]
        : undefined) ??
      "Open the full page detail to review the current scan record.",
  };
}

export function issuesForPage(
  data: OverviewResponse,
  page: OverviewResponse["pages"][number],
): Issue[] {
  const rows = data.issues.filter(
    (issue) =>
      isOpen(issue) &&
      issue.pages.some((link) => issueLinkMatchesPage(link, page)),
  );
  return rankBySeverity(rows);
}

function isWeakPage(
  page: OverviewResponse["pages"][number],
  lens: PageMapLens = PAGE_MAP_LENS.HUMAN,
): boolean {
  const score =
    lens === PAGE_MAP_LENS.AGENT
      ? page.agentScore
      : pageContentFitnessScore(page);
  return pageHealthClass(score) === HEALTH_STATE.POOR;
}

export function isStrategicPage(
  data: OverviewResponse,
  page: OverviewResponse["pages"][number],
): boolean {
  return isImportantPath(importantPagePaths(data), page.path);
}

export function pageContextLabel(context: PageContext): string {
  if (context.weak && context.strategic) {
    return "Needs attention - linked to a priority issue";
  }
  if (context.weak) {
    return "Needs attention";
  }
  if (context.strategic) {
    return "Linked to a priority issue";
  }
  return "Monitored page";
}

export function pageContextImpact(context: PageContext): string {
  if (context.topIssue) {
    return `${context.topIssueUniqueAffectedPageCount} unique affected page${
      context.topIssueUniqueAffectedPageCount === 1 ? "" : "s"
    } in this scan for the top linked issue`;
  }
  if (context.weak) {
    return `${lensLabel(context.lens)} score is below the current watch threshold.`;
  }
  return "No open issue is linked directly to this page in this scan.";
}

export function ownerForIssue(issue: Issue | null): string {
  return issue?.ownerName ?? "Unassigned";
}

export function actionForPage(context: PageContext): string {
  return context.recommendation;
}

function pageDetailHasEvidence(detail: PageDetailResponse): boolean {
  if (detail.issues.some((issue) => issue.evidenceSnippet)) {
    return true;
  }
  return Boolean(detail.scrapeProof);
}
